import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ExternalLink, X } from 'lucide-react'
import type { ChartSpec, Timeframe } from '@shared/chart-spec'
import type { CompanyProfile, Quote, TaAction, TaSummary, TechnicalAnalysis } from '@shared/types'
import { useStore } from '../../store'
import { api } from '../../lib/api'
import { LightweightChart } from '../chart/LightweightChart'
import { useExtendedQuotes, useLiveQuotes, useMarketSession } from '../../hooks/useMarketData'
import { yahooQuoteUrl } from '../../lib/remark-cashtags'
import { displaySymbol, normalizeSymbol } from '../../lib/symbol'
import { dirClass, fmtCompact, fmtPct, fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'

// Range buttons map to a lookback window + a sensible candle interval (so "1M"
// means the last month, not "monthly candles forever").
const RANGES: { key: string; tf: Timeframe; days: number }[] = [
  { key: '1D', tf: '15m', days: 2 },
  { key: '1W', tf: '1h', days: 8 },
  { key: '1M', tf: '1D', days: 31 },
  { key: '3M', tf: '1D', days: 93 },
  { key: '1Y', tf: '1D', days: 366 },
  { key: 'ALL', tf: '1W', days: 0 },
]

const ACTION_LABEL: Record<TaAction, string> = {
  buy: 'Buy',
  sell: 'Sell',
  neutral: 'Neutral',
  overbought: 'Overbought',
  oversold: 'Oversold',
  'high-vol': 'High Volatility',
  'low-vol': 'Less Volatility',
}
const SUMMARY_LABEL: Record<TaSummary, string> = {
  'strong-sell': 'Strong Sell',
  sell: 'Sell',
  neutral: 'Neutral',
  buy: 'Buy',
  'strong-buy': 'Strong Buy',
}
// Buy/Oversold read bullish (up); Sell/Overbought read bearish (down); rest neutral.
function actionTone(a: TaAction): string {
  if (a === 'buy' || a === 'oversold') return 'text-up'
  if (a === 'sell' || a === 'overbought') return 'text-down'
  return 'text-fg-subtle'
}
function summaryChip(s: TaSummary): string {
  if (s === 'strong-buy') return 'bg-up text-[var(--color-base)]'
  if (s === 'buy') return 'text-up border border-[color:var(--color-up)]'
  if (s === 'strong-sell') return 'bg-down text-[var(--color-base)]'
  if (s === 'sell') return 'text-down border border-[color:var(--color-down)]'
  return 'text-fg-subtle border border-[var(--line-soft)]'
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-fg-faint">{label}</div>
      <div className="num mt-0.5 truncate text-[12.5px] text-fg">{value}</div>
    </div>
  )
}

export function SymbolModal() {
  const sym = useStore((s) => s.symbolModal)
  const close = useStore((s) => s.closeSymbol)
  return <AnimatePresence>{sym && <SymbolModalInner key={sym} symbol={sym} onClose={close} />}</AnimatePresence>
}

function SymbolModalInner({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null)
  const [ta, setTa] = useState<TechnicalAnalysis | null>(null)
  const [taErr, setTaErr] = useState<string | null>(null)
  const [quote, setQuote] = useState<Quote | null>(null)
  const [rangeKey, setRangeKey] = useState('1M')

  const full = normalizeSymbol(symbol)
  const disp = displaySymbol(symbol)
  const session = useMarketSession()
  const live = useLiveQuotes([full])
  const ext = useExtendedQuotes([full], session)

  useEffect(() => {
    let alive = true
    setProfile(null)
    setTa(null)
    setTaErr(null)
    setQuote(null)
    api.profile(symbol).then((p) => alive && setProfile(p)).catch(() => {})
    api.technicals(symbol).then((t) => alive && setTa(t)).catch((e: Error) => alive && setTaErr(e.message))
    api.quote(symbol).then((q) => alive && setQuote(q)).catch(() => {})
    return () => {
      alive = false
    }
  }, [symbol])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const spec = useMemo<ChartSpec>(() => {
    const r = RANGES.find((x) => x.key === rangeKey) ?? RANGES[2]
    const from = r.days ? new Date(Date.now() - r.days * 86_400_000).toISOString().slice(0, 10) : undefined
    return {
      version: '1',
      symbol: full,
      type: 'area',
      timeframe: r.tf,
      range: from ? { from } : undefined,
      panes: [{ kind: 'volume' }],
    } as ChartSpec
  }, [full, rangeKey])

  const price = live[full]?.price ?? ext[full]?.price ?? quote?.price ?? ta?.price ?? null
  // Outside regular hours, measure the move against the REGULAR-session close (the extended
  // quote's prevClose) so "After-hours +X%" is the move since the 16:00 close — not since
  // YESTERDAY's close (quote.previousClose), which double-counts the regular day's move.
  // ext[full] only exists in pre/after/closed, so regular sessions still use the day quote.
  const prevClose = ext[full]?.prevClose ?? quote?.previousClose ?? null
  const change = price != null && prevClose != null ? price - prevClose : quote?.change ?? null
  const changePct = price != null && prevClose ? (change! / prevClose) * 100 : quote?.changePct ?? null
  const extActive = session !== 'regular' && !!ext[full]
  // Yahoo-style dual readout outside regular hours: the regular CLOSE + day change, AND the
  // extended (pre/after) price + its move since that close — so a name down -5% on the day
  // that's +0.3% after-hours shows BOTH, not just the misleading green +0.3%.
  const extPrice = extActive ? live[full]?.price ?? ext[full]?.price ?? null : null
  const extBase = quote?.price ?? ext[full]?.prevClose ?? null // the regular-session close
  const extChg = extPrice != null && extBase != null ? extPrice - extBase : null
  const extPct = extPrice != null && extBase ? (extChg! / extBase) * 100 : null
  const extTime = live[full]?.time ?? ext[full]?.time ?? null // epoch ms of the last extended trade
  const etClock = (ms: number) =>
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(ms)) + ' ET'

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-3 sm:items-center sm:p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/55" onClick={onClose} />
      <motion.div
        initial={{ y: 16, scale: 0.985, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 16, scale: 0.985, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
        className="glass relative my-auto flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden"
        style={{ borderRadius: 'var(--radius-lg)' }}
      >
        {/* header */}
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--line-soft)] px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            {profile?.logo ? (
              <img src={profile.logo} alt="" className="h-9 w-9 shrink-0 rounded-lg bg-white object-contain p-0.5" />
            ) : (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-3)] text-[14px] font-semibold text-fg-muted">
                {disp.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="num text-[15px] font-semibold text-fg">{disp}</span>
                {extActive && (
                  <span className="rounded bg-[var(--color-surface-3)] px-1 text-[9px] font-semibold uppercase text-fg-subtle">
                    {session === 'pre' ? 'PRE' : 'AH'}
                  </span>
                )}
              </div>
              <div className="truncate text-[12px] text-fg-subtle">{profile?.name ?? '—'}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight">
              {extActive && quote ? (
                <>
                  {/* regular session close + day change */}
                  <div className="flex flex-wrap items-center justify-end gap-x-1.5">
                    <span className="num text-[15px] font-semibold text-fg">{fmtPrice(quote.price, '$')}</span>
                    {quote.changePct != null && (
                      <span className={cn('num text-[11.5px]', dirClass(quote.change ?? 0))}>
                        {(quote.change ?? 0) >= 0 ? '+' : ''}
                        {fmtPrice(quote.change ?? 0, '$')} ({fmtPct(quote.changePct)})
                      </span>
                    )}
                    <span className="w-full text-[10px] text-fg-faint">At close · 4:00 PM ET</span>
                  </div>
                  {/* extended (pre / after-hours) price + its move since the close */}
                  <div className="mt-1 flex flex-wrap items-center justify-end gap-x-1.5">
                    <span className="num text-[14px] font-semibold text-fg-muted">{extPrice != null ? fmtPrice(extPrice, '$') : '—'}</span>
                    {extPct != null && (
                      <span className={cn('num text-[11.5px]', dirClass(extChg ?? 0))}>
                        {(extChg ?? 0) >= 0 ? '+' : ''}
                        {fmtPrice(extChg ?? 0, '$')} ({fmtPct(extPct)})
                      </span>
                    )}
                    <span className="w-full text-[10px] text-fg-faint">
                      {session === 'pre' ? 'Pre-market' : 'After hours'}
                      {extTime ? ` · ${etClock(extTime)}` : ''}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="num text-[16px] font-semibold text-fg">{price != null ? fmtPrice(price, '$') : '—'}</div>
                  {change != null && changePct != null && (
                    <div className={cn('num text-[11.5px]', dirClass(change))}>
                      {change >= 0 ? '+' : ''}
                      {fmtPrice(change, '$')} ({fmtPct(changePct)})
                    </div>
                  )}
                </>
              )}
            </div>
            <a
              href={yahooQuoteUrl(disp)}
              target="_blank"
              rel="noreferrer noopener"
              title="Open on Yahoo Finance"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-subtle transition-colors hover:bg-[var(--color-surface-2)] hover:text-fg"
            >
              <ExternalLink size={16} />
            </a>
            <button onClick={onClose} title="Close" className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-fg-subtle transition-colors hover:bg-[var(--color-surface-2)] hover:text-fg">
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-4">
          {/* chart range */}
          <div className="mb-1 flex items-center gap-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRangeKey(r.key)}
                className={cn(
                  'rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                  rangeKey === r.key ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg',
                )}
              >
                {r.key}
              </button>
            ))}
          </div>
          <LightweightChart spec={spec} livePrice={price} hideHeader />

          {/* technicals */}
          {taErr ? (
            <div className="card mt-3 px-4 py-6 text-center text-[12.5px] text-fg-subtle">{taErr}</div>
          ) : !ta ? (
            <div className="card mt-3 px-4 py-6 text-center text-[12.5px] text-fg-subtle">
              <span className="animate-pulse">Analyzing {disp}…</span>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              {/* overall summary + commentary */}
              <div className="card flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-wide text-fg-subtle">Technical summary</span>
                  <span className={cn('rounded-md px-2 py-0.5 text-[12px] font-semibold', summaryChip(ta.overall))}>
                    {SUMMARY_LABEL[ta.overall]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ta.commentary.map((c, i) => (
                    <span key={i} className="rounded-full border border-[var(--line-soft)] bg-[var(--color-surface-2)] px-2 py-0.5 text-[11px] text-fg-muted">
                      {c}
                    </span>
                  ))}
                </div>
                <span className="ml-auto text-[10.5px] text-fg-faint">as of {ta.asOf} close</span>
              </div>

              <div className="grid gap-3 lg:grid-cols-2">
                {/* oscillators */}
                <div className="card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-[var(--line-soft)] px-3 py-2">
                    <span className="text-[12px] font-semibold text-fg">Oscillators</span>
                    <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', summaryChip(ta.oscillators.summary))}>
                      {SUMMARY_LABEL[ta.oscillators.summary]}
                    </span>
                  </div>
                  <div className="px-3 py-1.5 text-[10.5px] text-fg-subtle">
                    Buy {ta.oscillators.buy} · Neutral {ta.oscillators.neutral} · Sell {ta.oscillators.sell}
                  </div>
                  <table className="w-full text-[12px]">
                    <tbody>
                      {ta.oscillators.rows.map((r) => (
                        <tr key={r.name} className="border-t border-[var(--separator)]">
                          <td className="px-3 py-1.5 text-fg-muted">{r.name}</td>
                          <td className="num px-3 py-1.5 text-right text-fg">{r.value ?? '—'}</td>
                          <td className={cn('px-3 py-1.5 text-right font-medium', actionTone(r.action))}>{ACTION_LABEL[r.action]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* moving averages */}
                <div className="card overflow-hidden">
                  <div className="flex items-center justify-between border-b border-[var(--line-soft)] px-3 py-2">
                    <span className="text-[12px] font-semibold text-fg">Moving Averages</span>
                    <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', summaryChip(ta.movingAverages.summary))}>
                      {SUMMARY_LABEL[ta.movingAverages.summary]}
                    </span>
                  </div>
                  <div className="px-3 py-1.5 text-[10.5px] text-fg-subtle">
                    Buy {ta.movingAverages.buy} · Sell {ta.movingAverages.sell}
                  </div>
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-[9.5px] uppercase tracking-wide text-fg-faint">
                        <th className="px-3 py-1 text-left font-medium"></th>
                        <th className="px-3 py-1 text-right font-medium">Simple</th>
                        <th className="px-3 py-1 text-right font-medium">Exponential</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ta.movingAverages.rows.map((r) => (
                        <tr key={r.period} className="border-t border-[var(--separator)]">
                          <td className="px-3 py-1.5 text-fg-muted">MA{r.period}</td>
                          <td className="num px-3 py-1.5 text-right">
                            <span className="text-fg">{r.sma ?? '—'}</span>{' '}
                            <span className={cn('text-[10px]', actionTone(r.smaAction))}>{ACTION_LABEL[r.smaAction]}</span>
                          </td>
                          <td className="num px-3 py-1.5 text-right">
                            <span className="text-fg">{r.ema ?? '—'}</span>{' '}
                            <span className={cn('text-[10px]', actionTone(r.emaAction))}>{ACTION_LABEL[r.emaAction]}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* pivot points */}
              <div className="card overflow-hidden">
                <div className="border-b border-[var(--line-soft)] px-3 py-2 text-[12px] font-semibold text-fg">Pivot Points</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead>
                      <tr className="text-[9.5px] uppercase tracking-wide text-fg-faint">
                        <th className="px-3 py-1.5 text-left font-medium">Name</th>
                        {['S3', 'S2', 'S1', 'Pivot', 'R1', 'R2', 'R3'].map((h) => (
                          <th key={h} className="px-3 py-1.5 text-right font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ta.pivots.map((p) => (
                        <tr key={p.name} className="border-t border-[var(--separator)]">
                          <td className="px-3 py-1.5 text-fg-muted">{p.name}</td>
                          {[p.s3, p.s2, p.s1, p.pivot, p.r1, p.r2, p.r3].map((v, i) => (
                            <td key={i} className={cn('num px-3 py-1.5 text-right', i === 3 ? 'font-semibold text-fg' : 'text-fg-muted')}>
                              {v ?? '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* company info */}
          {profile && (
            <div className="card mt-3 p-3.5">
              <div className="mb-2.5 text-[11px] uppercase tracking-wide text-fg-subtle">Company</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
                {profile.sector && <Stat label="Sector" value={profile.sector} />}
                {profile.industry && <Stat label="Industry" value={profile.industry} />}
                {profile.marketCap != null && <Stat label="Market cap" value={`$${fmtCompact(profile.marketCap)}`} />}
                {profile.peRatio != null && <Stat label="P/E" value={profile.peRatio.toFixed(2)} />}
                {profile.eps != null && <Stat label="EPS" value={`$${profile.eps.toFixed(2)}`} />}
                {profile.beta != null && <Stat label="Beta" value={profile.beta.toFixed(2)} />}
                {profile.dividendYield != null && profile.dividendYield > 0 && (
                  <Stat label="Div yield" value={`${(profile.dividendYield * 100).toFixed(2)}%`} />
                )}
                {profile.week52Low != null && profile.week52High != null && (
                  <Stat label="52-wk range" value={`${fmtPrice(profile.week52Low)} – ${fmtPrice(profile.week52High)}`} />
                )}
                {profile.employees != null && <Stat label="Employees" value={fmtCompact(profile.employees)} />}
                {profile.country && <Stat label="Country" value={profile.country} />}
              </div>
              {profile.description && <Description text={profile.description} />}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

function Description({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  const long = text.length > 280
  return (
    <p className="mt-3 text-[12px] leading-relaxed text-fg-muted">
      {open || !long ? text : `${text.slice(0, 280)}…`}
      {long && (
        <button onClick={() => setOpen((o) => !o)} className="ml-1 text-fg-subtle underline hover:text-fg">
          {open ? 'less' : 'more'}
        </button>
      )}
    </p>
  )
}
